import React, { createContext, useContext } from "react";
import { getLocalStorageItem, setLocalStorageItem } from "./useLocalStorage";
import useReducerWithSideEffects, {
  UpdateWithSideEffect,
} from "use-reducer-with-side-effects";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "./useSessionStorage";

const AppContext = createContext();

const reducer = (prevState, action) => {
  const { type } = action;
  if (type === SET_TOKEN_LOCAL) {
    const {
      payload: { access, refresh, userId, completedCallback },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        access,
        refresh,
        userId,
        completedCallback,
      },
      (state, dispatch) => {
        setLocalStorageItem("access", access);
        setLocalStorageItem("refresh", refresh);
        setLocalStorageItem("userId", userId);
        completedCallback();
      }
    );
  } else if (type === SET_TOKEN_SESSION) {
    const {
      payload: { access, refresh, userId, completedCallback },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        access,
        refresh,
        userId,
        completedCallback,
      },
      (state, dispatch) => {
        setSessionStorageItem("access", access);
        setSessionStorageItem("refresh", refresh);
        setSessionStorageItem("userId", userId);
        completedCallback();
      }
    );
  } else if (type === SET_AUTHORITY_LOCAL) {
    const {
      payload: { isSuperUser, externalApiEnabled, remsEnabled, businessId },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        isSuperUser,
        externalApiEnabled,
        remsEnabled,
        businessId,
      },
      (state, dispatch) => {
        setLocalStorageItem("isSuperUser", isSuperUser);
        setLocalStorageItem("externalApiEnabled", externalApiEnabled);
        setLocalStorageItem("remsEnabled", remsEnabled);
        setLocalStorageItem("businessId", businessId);
      }
    );
  } else if (type === SET_AUTHORITY_SESSION) {
    const {
      payload: { isSuperUser, externalApiEnabled, remsEnabled, businessId },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        isSuperUser,
        externalApiEnabled,
        remsEnabled,
        businessId,
      },
      (state, dispatch) => {
        setSessionStorageItem("isSuperUser", isSuperUser);
        setSessionStorageItem("externalApiEnabled", externalApiEnabled);
        setSessionStorageItem("remsEnabled", remsEnabled);
        setSessionStorageItem("businessId", businessId);
      }
    );
  } else if (type === DELETE_TOKEN) {
    return UpdateWithSideEffect(
      {
        ...prevState,
        access: "",
        refresh: "",
        userId: "",
        isSuperUser: false,
        externalApiEnabled: false,
        remsEnabled: false,
        businessId: null,
      },
      (state, dispatch) => {
        setLocalStorageItem("access", "");
        setLocalStorageItem("refresh", "");
        setLocalStorageItem("userId", "");
        setLocalStorageItem("isSuperUser", false);
        setLocalStorageItem("externalApiEnabled", false);
        setLocalStorageItem("remsEnabled", false);
        setLocalStorageItem("businessId", null);

        setSessionStorageItem("access", "");
        setSessionStorageItem("refresh", "");
        setSessionStorageItem("userId", "");
        setSessionStorageItem("isSuperUser", false);
        setSessionStorageItem("externalApiEnabled", false);
        setSessionStorageItem("remsEnabled", false);
        setSessionStorageItem("businessId", null);
      }
    );
  } else if (type === SET_CURRENT_URL) {
    const { payload: currentUrl } = action;
    return UpdateWithSideEffect(
      {
        ...prevState,
        currentUrl,
      },
      (state, dispatch) => {
        setLocalStorageItem("currentUrl", currentUrl);
      }
    );
  }

  return prevState;
};

export const AppProvider = ({ children }) => {
  let defaultaccess = getSessionStorageItem("access", "");
  let defaultrefresh = getSessionStorageItem("refresh", "");
  let defaultUserId = getSessionStorageItem("userId", "");
  let defaultExternalApiEnabled = getSessionStorageItem(
    "externalApiEnabled",
    false
  );
  let defaultRemsEnabled = getSessionStorageItem("remsEnabled", false);
  let defaultIsSuperUser = getSessionStorageItem("isSuperUser", false);
  let defaultBusinessId = getSessionStorageItem("businessId", null);
  if (!defaultaccess) {
    defaultaccess = getLocalStorageItem("access", "");
    defaultrefresh = getLocalStorageItem("refresh", "");
    defaultUserId = getLocalStorageItem("userId", "");
    defaultExternalApiEnabled = getLocalStorageItem(
      "externalApiEnabled",
      false
    );
    defaultRemsEnabled = getLocalStorageItem("remsEnabled", false);
    defaultIsSuperUser = getLocalStorageItem("isSuperUser", false);
    defaultBusinessId = getLocalStorageItem("businessId", null);
  }
  const [store, dispatch] = useReducerWithSideEffects(reducer, {
    access: defaultaccess,
    refresh: defaultrefresh,
    isAuthenticated: defaultaccess.length > 0,
    userId: defaultUserId,
    externalApiEnabled: defaultExternalApiEnabled,
    remsEnabled: defaultRemsEnabled,
    isSuperUser: defaultIsSuperUser,
    businessId: defaultBusinessId,
    currentUrl: "",
  });

  return (
    <AppContext.Provider value={{ store, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

//Actions
const SET_TOKEN_LOCAL = "APP/SET_TOKEN";
const SET_TOKEN_SESSION = "APP/SET_TOKEN_SESSION";
const SET_AUTHORITY_LOCAL = "APP/SET_AUTHORITY";
const SET_AUTHORITY_SESSION = "APP/SET_AUTHORITY_SESSION";
const DELETE_TOKEN = "APP/DELETE_TOKEN";
const SET_CURRENT_URL = "APP/SET_CURRENT_URL";

//Action Creators
export const setTokenInLocal = (
  access,
  refresh,
  userId,
  completedCallback
) => ({
  type: SET_TOKEN_LOCAL,
  payload: {
    access,
    refresh,
    userId,
    completedCallback,
  },
});
export const setTokenInSession = (
  access,
  refresh,
  userId,
  completedCallback
) => ({
  type: SET_TOKEN_SESSION,
  payload: {
    access,
    refresh,
    userId,
    completedCallback,
  },
});
export const setAuthorityInLocal = (
  isSuperUser,
  externalApiEnabled,
  remsEnabled,
  businessId
) => ({
  type: SET_AUTHORITY_LOCAL,
  payload: {
    isSuperUser,
    externalApiEnabled,
    remsEnabled,
    businessId,
  },
});
export const setAuthorityInSession = (
  isSuperUser,
  externalApiEnabled,
  remsEnabled,
  businessId
) => ({
  type: SET_AUTHORITY_SESSION,
  payload: {
    isSuperUser,
    externalApiEnabled,
    remsEnabled,
    businessId,
  },
});
export const deleteToken = () => ({ type: DELETE_TOKEN });
export const setCurrentURL = (currentUrl) => ({
  type: SET_CURRENT_URL,
  payload: currentUrl,
});
